import React from 'react';

type PodcastPlatform = 'youtube' | 'spotify';

interface PodcastItem {
  id: string;
  title: string;
  description?: string;
  platform: PodcastPlatform;
  url: string;
}

interface PodcastSectionProps {
  title?: string;
  podcasts?: PodcastItem[];
}

const PODCAST_LINKS: PodcastItem[] = [
  {
    id: '1',
    title: 'BSides Bangalore X W3-CS Podcast - Vishal Salvi',
    description: 'Conference highlights and community stories from BSides Bangalore.',
    platform: 'youtube',
    url: 'https://youtu.be/qhCLrcMRU0M?si=388-laeDs6PoKH7s',
  },
  {
    id: '2',
    title: 'BSides Bangalore X W3-CS Podcast - Suvabrata Sinha',
    description: 'Conference highlights and community stories from BSides Bangalore.',
    platform: 'youtube',
    url: 'https://youtu.be/7RY0l5_Y1FU?si=p00KX2Wbsr4NXLyw',
  }
];

/*
sample Spotify podcast item for reference:

  {
    id: 'sample-spotify-podcast',
    title: 'Sample Spotify Podcast',
    description: 'Replace this with a Spotify episode, show, or playlist link.',
    platform: 'spotify',
    url: 'https://open.spotify.com/show/0ofXAdFIQQRsCYj9754UFx',
  },
*/


const getYouTubeEmbedUrl = (url: string): string => {
  if (url.includes('youtube.com/embed/')) {
    return url;
  }

  if (url.includes('youtube.com/watch')) {
    const parsedUrl = new URL(url);
    const videoId = parsedUrl.searchParams.get('v');
    return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
  }

  if (url.includes('youtu.be/')) {
    const videoId = url.split('youtu.be/')[1]?.split('?')[0]?.split('/')[0];
    return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
  }

  return /^[a-zA-Z0-9_-]{11}$/.test(url.trim())
    ? `https://www.youtube.com/embed/${url.trim()}`
    : '';
};

const getSpotifyEmbedUrl = (url: string): string => {
  if (url.includes('open.spotify.com/embed/')) {
    return url;
  }

  const match = url.match(/open\.spotify\.com\/(episode|show|playlist)\/([^?]+)/);
  return match ? `https://open.spotify.com/embed/${match[1]}/${match[2]}` : '';
};

const getEmbedUrl = (item: PodcastItem): string => {
  return item.platform === 'spotify'
    ? getSpotifyEmbedUrl(item.url)
    : getYouTubeEmbedUrl(item.url);
};

const PodcastSection: React.FC<PodcastSectionProps> = ({
  title = 'Featured Podcasts',
  podcasts = PODCAST_LINKS,
}) => {
  const visiblePodcasts = podcasts.filter((item) => item.url && item.platform);

  if (visiblePodcasts.length === 0) {
    return null;
  }

  return (
    <section className="podcast-section" aria-labelledby="podcast-section">
      <h3 id="podcast-section">{title}</h3>
      <div className="podcast-list">
        {visiblePodcasts.map((podcast) => {
          const embedUrl = getEmbedUrl(podcast);

          return (
            <article className="podcast-card" key={podcast.id}>
              {embedUrl ? (
                <iframe
                  className="podcast-embed"
                  src={embedUrl}
                  title={podcast.title}
                  loading="lazy"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <a className="podcast-fallback" href={podcast.url} target="_blank" rel="noopener noreferrer">
                  Open {podcast.platform}
                </a>
              )}
              <div className="podcast-details">
                <h4>{podcast.title}</h4>
                {podcast.description && <p>{podcast.description}</p>}
                <a href={podcast.url} target="_blank" rel="noopener noreferrer">
                  Listen on {podcast.platform === 'spotify' ? 'Spotify' : 'YouTube'}
                </a>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default PodcastSection;
